import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactDOM from 'react-dom';

import FormattedSummary from './FormattedSummary';

// Utility to render different media types inline or fallback to download links
function renderMedia(url) {
  const ext = url.split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','svg','webp'].includes(ext)) {
    return <img src={url} alt="Media" style={{  maxHeight: '100%' }} />;
  }
  if (['mp4','webm','ogg'].includes(ext)) {
    return (
      <video controls style={{ maxWidth: '100%' }}>
        <source src={url} type={`video/${ext}`} />
        Your browser doesn’t support this video format.
      </video>
    );
  }
  if (['mp3','wav','ogg'].includes(ext)) {
    return <audio controls src={url}>Your browser doesn’t support this audio format.</audio>;
  }
  // if (ext === 'pdf') {
  //   const pdfUrl = `${url}#toolbar=0`;
  //   return (
  //     <iframe
  //       src={pdfUrl}
  //       title="PDF Viewer"
  //       style={{
  //         width: '100%',
  //         height: '90vh',
  //         maxWidth: '800px',
  //         maxHeight: '90vh',
  //         border: 0,
  //       }}
  //     >
  //       <p>
  //         It looks like your browser can’t display PDFs inline.&nbsp;
  //       </p>
  //     </iframe>
  //   );
  // }
  if (ext === 'pdf') {
    return (
      <p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ 
            display: 'inline-block', 
            padding: '0.5em 1em', 
            background: '#007bff', 
            color: '#fff', 
            borderRadius: '4px',
            textDecoration: 'none'
          }}
        >
          View PDF in new tab
        </a>
      </p>
    );
  }
  // Fallback for any other file types
  return (
    <p>
      <a href={url} target="_blank" rel="noopener noreferrer">
        Download file
      </a>
    </p>
  );
}


function CaseSimulation() {
  const { id } = useParams();
  const [caseTitle, setCaseTitle] = useState('');
  const [userInput, setUserInput] = useState('');
  const [proctorResponse, setProctorResponse] = useState('');
  const [audioSrc, setAudioSrc] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [caseComplete, setCaseComplete] = useState(false);
  const [criticalActions, setCriticalActions] = useState([]);
  const [matchedActions, setMatchedActions] = useState([]);
  const [caseProgress, setCaseProgress] = useState(null);
  // const [isSimulationActive, setIsSimulationActive] = useState(true);
  const [summary, setSummary] = useState('');
  const [caseNumber, setCaseNumber] = useState(null);

  const [proctorOutputs, setProctorOutputs] = useState([]);
  const [userInputs, setUserInputs] = useState([]);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [warningAccepted, setWarningAccepted] = useState(false);

  const [images, setImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
    
      const togglePopup = () => {
        setIsPopupVisible(!isPopupVisible);
      };
  
  // useEffect(() => {
  //   const fetchCaseAndLogAttempt = async () => {
  //     try {
  //       const response = await axios.get(`/cases/${id}`, { withCredentials: true });
  //       setCaseTitle(response.data.title);
  //       setCriticalActions(response.data.criticalActions || []);
  //     } catch (error) {
  //       if (error.response && error.response.status === 403) {
  //         alert('You have no attempts remaining for this case.');
  //         // Redirect user back to the dashboard
  //         window.location.href = '/dashboard';
  //       } else {
  //         console.error('Failed to load case and log attempt', error);
  //       }
  //     }
  //   };
  //   fetchCaseAndLogAttempt();
  // }, [id]);
  
  useEffect(() => {
    if (!warningAccepted) {
      const userConfirmed = window.confirm(
        "Starting this case will count as one of your attempts. Do you wish to continue?"
      );
      if (!userConfirmed) {
        navigate('/dashboard');
        return;
      }
      setWarningAccepted(true); // Allow fetching the case
    }

    const fetchCaseData = async () => {
      try {
        const response = await axios.get(`/cases/${id}`, { withCredentials: true });
        setCaseTitle(response.data.title);
        setCriticalActions(response.data.criticalActions || []);
        setImages(response.data.images);
        setSummary(response.data.summary);
        setCaseNumber(response.data.caseNumber);
      } catch (error) {
        if (error.response?.status === 403) {
          alert('No attempts remaining for this case.');
          navigate('/dashboard');
        } else {
          console.error('Error fetching case:', error);
        }
      }
    };

    if (warningAccepted) {
      fetchCaseData();
    }
  }, [id, warningAccepted, navigate]);

  const handleImageClick = (imageUrl) => {
    setCurrentImage(imageUrl); // Open the image in a modal or display on the page
  };

const hasImages = Array.isArray(images) && images.length > 0;

// Case type flag
const isClinicalDecisionMaking = Number(caseNumber) < 1000;

// Only clinical decision-making cases have Media 0 stored as "last image"
const media0Url =
  isClinicalDecisionMaking && hasImages ? images[images.length - 1] : null;

// If clinical decision-making: Media 1..N excludes last
// Otherwise (caseNumber cases): Media 1..N should include ALL images
const filteredImages =
  hasImages
    ? (isClinicalDecisionMaking ? images.slice(0, -1) : images)
    : [];


  const sendInput = async (inputValue = null) => {
    // const inputToSend = inputValue !== null ? inputValue : userInput;
    const inputToSend = inputValue !== null ? String(inputValue) : String(userInput);
    console.log("User Input:");
    console.log(userInput);
    if (!inputToSend.trim()) return;
    setLoading(true); // Show spinner

    // Check input limit
    if (userInputs.length >= 40) {
      alert('You have reached the maximum number of inputs for this attempt. Please complete the case or start a new attempt.');
      await abortSimulation(); //abort automatically if limit is reached 
      return;
    }

    try {
      const response = await axios.post('/cases/generate-response', {
        userInput: inputToSend,
        caseId: id,
        isMuted: isMuted,
      }, { withCredentials: true });

      setProctorResponse(response.data.aiResponse);
      setProctorOutputs((prev) => [...prev, response.data.aiResponse]);
      setUserInputs((prev) => [...prev, userInput]);

      if (/case has ended/i.test(response.data.aiResponse)) {
        // showCompletionMessage();
        setCaseComplete(true);
        submitCaseCompletion();
      } else if (!isMuted && response.data.audioContent) {
        const audioContent = response.data.audioContent;
        const audioBlob = new Blob([Uint8Array.from(atob(audioContent), c => c.charCodeAt(0))], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioSrc(audioUrl);
      }
    } catch (error) {
      console.error('Error generating response', error);
    } finally {
      setLoading(false); //Hide spinner
    }

    setUserInput(''); // Clear input after submission
  };

  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Sorry, your browser does not support speech recognition.');
      return;
    }
  
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
  
    // Clear the input box and set placeholder to "Listening..." when recording starts
    const userInputElement = document.getElementById('userInput');
    setUserInput('');  // Clear the input box through state
    userInputElement.placeholder = "Listening...";
  
    recognition.start();
  
    recognition.onresult = function (event) {
      const spokenText = event.results[0][0].transcript;
  
      // Set the recognized text in the input box so the user can see it
      setUserInput(spokenText);
  
      // Call sendInput with the recognized text directly
      // sendInput(spokenText);
    };
  
    recognition.onerror = function (event) {
      console.error('Speech recognition error:', event.error);
      alert('Error with speech recognition: ' + event.error);
    };
  
    recognition.onend = function () {
      // Reset the placeholder back to the default value after recognition ends
      userInputElement.placeholder = "Type your question here or Speak with mic button";
    };
  };  

  const submitCaseCompletion = async () => {
    try {
      const response = await axios.post(
        `/cases/${id}/complete`,
        { proctorOutputs, userInputs },
        { withCredentials: true }
      );

      setMatchedActions(response.data.matchedActions);
      setCaseProgress(response.data.caseProgress);
      setCaseComplete(true);

      console.log("Matched Actions:", response.data.matchedActions);
      console.log("Case Progress:", response.data.caseProgress);

      // Reset user and proctor inputs for the next attempt
      setUserInputs([]);
      setProctorOutputs([]);
      
      // Call showScores with the updated caseProgress

        showScores(response.data.caseProgress);

    } catch (error) {
      console.error('Error submitting case completion:', error);
    }
  };

  function showCompletionMessage() {
    const contentElement = document.querySelector('.content');
    if (contentElement) {
      contentElement.innerHTML = `
        <div class="completion">
          <div class="checkmark-circle">
            <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
              <circle class="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none"/>
              <path class="checkmark-check" fill="none" d="M14 27l7 7 15-15"/>
            </svg>
          </div>
          <h2>Case Complete</h2>
        </div>
      `;
    }
  }

  function showScores(updatedCaseProgress) {
    if (!updatedCaseProgress || !updatedCaseProgress.attempts) {
      console.error("caseProgress is missing or malformed:", updatedCaseProgress);
      return;
    }
    console.log('show scores');
    console.log(updatedCaseProgress);
    const resultsElement = document.querySelector('.results');
    if (!resultsElement) {
      console.error(".results element not found");
      return;
    }

    const gridHTML = `
      <div class="results">
        <h3>Your Score</h3>
        <table class="critical-actions-grid">
          <thead>
            <tr>
              <th>Critical Action</th>
              ${updatedCaseProgress.attempts.map(
                (attempt, index) => `<th>Attempt ${index + 1}</th>`
              ).join('')}
            </tr>
          </thead>
          <tbody>
            ${criticalActions.map((action) => `
              <tr>
                <td>${action}</td>
                ${updatedCaseProgress.attempts.map((attempt) => `
                  <td>
                    ${attempt.completedCriticalActions.includes(action) ? `
                      <svg class="small-checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" width="20" height="20">
                        <circle class="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none"/>
                        <path class="checkmark-check" fill="none" d="M14 27l7 7 15-15"/>
                      </svg>
                    ` : ''}
                  </td>
                `).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div id="summary-container"></div>
      </div>
    `;

    console.log(summary);
    resultsElement.innerHTML = gridHTML;
    renderFormattedSummary(summary);


  // 2) Create & append the dashboard button **after** summary
  const btnWrapper = document.createElement('div');
  btnWrapper.className = 'mt-6 flex justify-center';
  const btn = document.createElement('button');
  btn.textContent = 'Return to Dashboard';
  btn.className = 'px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded';
  btn.addEventListener('click', () => navigate('/dashboard'));
  btnWrapper.appendChild(btn);
  resultsElement.appendChild(btnWrapper);


  }



  function renderFormattedSummary(summary) {
    const summaryContainer = document.getElementById('summary-container');
    if (summaryContainer) {
      ReactDOM.render(<FormattedSummary summary={summary} />, summaryContainer);
    } else {
      console.error('Summary container not found');
    }
  }


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendInput();
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (!isMuted) setAudioSrc(''); // Remove audio playback
  };

  const abortSimulation = async () => {
    try {
          // 1) Fire off one last dummy prompt
    const dummyText = ' ';                    // whatever you like
    const resp = await axios.post(
      '/cases/generate-response',
      { userInput: dummyText, caseId: id, isMuted },
      { withCredentials: true }
    );

    // 2) Render that “AI” response exactly as if the user had typed it
    const ai = "END SIMULATION";
    setProctorResponse(ai);
    setProctorOutputs(prev => [...prev, ai]);
    setUserInputs(prev => [...prev, dummyText]);

      setLoading(true); // Show spinner
      // End the case by submitting completion
      await submitCaseCompletion(); // Call the existing completion logic
      setLoading(false); // Hide spinner
      alert('Simulation aborted. The case is now complete, and your score will be displayed.');
    } catch (error) {
      console.error('Error aborting simulation:', error);
    }
  };
  

  return (
    <div className="container">
      <h1>{caseTitle}</h1>

      {caseNumber > 1000 && !caseComplete && (
        <div className="mb-4 p-2 bg-yellow-100 text-yellow-800 rounded">
          Please begin by clicking Media 1 to review the Candidate Task Sheet. Start the simulation by introducing yourself.
        </div>
      )}
      {caseNumber < 1000 && !caseComplete && (
        <div className="mb-4 p-2 bg-yellow-100 text-yellow-800 rounded">
          Please review Media 0 (your task sheet), then introduce yourself to the proctor, and begin by asking which specific elements of the HPI you’d like to know. 
        </div>
      )}

      <div style={{ position: 'relative', padding: '20px' }}>
      {/* Top-right button */}
      {!caseComplete && (
      <button
        style={{
          position: 'absolute',
          top: '175px',
          right: '10px',
          padding: '10px',
          backgroundColor: '#007bff',
          color: '#fff',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
        onClick={togglePopup}
      >
        Quick Guide
      </button>
      )}
      {/* Popup Image */}
      {isPopupVisible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={togglePopup} // Close popup when clicking outside the image
        >
          <div
            style={{
              position: 'relative',
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '10px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <button
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: 'red',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                fontSize: '16px',
                cursor: 'pointer',
              }}
              onClick={togglePopup}
            >
              &times;
            </button>
{/* Popup PDF */}
{isPopupVisible && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '12px',               // mobile breathing room
    }}
    onClick={togglePopup}
  >
    <div
      style={{
        position: 'relative',
        backgroundColor: '#fff',
        width: 'min(960px, 100%)',    // responsive width
        height: 'min(85vh, 900px)',   // responsive height
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
      }}
      onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
    >
      {/* Header bar (mobile friendly) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <strong style={{ fontSize: '14px' }}>Quick Guide</strong>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Open in new tab as a fallback (esp. iOS Safari) */}
          <a
            href="/QuickGuide2026.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '13px',
              padding: '6px 10px',
              background: '#007bff',
              color: '#fff',
              borderRadius: '6px',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Open Fullscreen
          </a>

          <button
            onClick={togglePopup}
            aria-label="Close"
            style={{
              backgroundColor: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              width: '34px',
              height: '34px',
              fontSize: '18px',
              cursor: 'pointer',
              lineHeight: '34px',
            }}
          >
            ×
          </button>
        </div>
      </div>

      {/* PDF viewer area */}
      <iframe
        src="/QuickGuide2026.pdf#view=FitH"  // keep toolbar enabled; FitH helps mobile
        title="Quick Guide PDF"
        style={{
          width: '100%',
          height: 'calc(100% - 52px)', // subtract header height
          border: 'none',
        }}
      />
    </div>
  </div>
)}

          </div>
        </div>
      )}
    </div>
      {/* {isSimulationActive && ( */}
      {caseComplete ? (
        <>
      <div className="completion">
        <div className="checkmark-circle">
          <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
            <circle className="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none" />
            <path className="checkmark-check" fill="none" d="M14 27l7 7 15-15" />
          </svg>
        </div>
        <h2>Case Complete</h2>
      </div>
<div className="image-buttons">
  {isClinicalDecisionMaking && media0Url && (
    <button
      onClick={() => setCurrentImage(media0Url)}
      className="btn btn-secondary m-2"
    >
      Media 0
    </button>
  )}

  {filteredImages.map((url, index) => (
    <button
      key={index}
      onClick={() => setCurrentImage(url)}
      className="btn btn-secondary m-2"
    >
      Media {index + 1}
    </button>
  ))}
</div>


          {currentImage && (
            <div style={{ position:'fixed', top:0, left:0, width:'100vw', height:'100vh', backgroundColor:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', padding:'1rem', zIndex:1000 }}>
              <div style={{ position:'relative', maxWidth:'800px', height:'100%', maxHeight:'90vh' }}>
                <button onClick={() => setCurrentImage(null)} style={{ position:'absolute', top:'-1.1rem', right:'-1rem', backgroundColor:'rgba(0,0,0,0.8)', color:'#fff', border:'none', borderRadius:'50%', width:'35px', height:'35px', fontSize:'18px', cursor:'pointer', zIndex:1001 }}>&times;</button>
                {renderMedia(currentImage)}
              </div>
            </div>
          )}
      </>
    ) : (
      <div className="content">
        <textarea
          id="userInput"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Type your question here or Speak with mic button (desktop only)"
          rows="4"
          className="input-box"
          maxLength={"1200"}
          disabled={loading}
        />
        <div className="input-info">
          <p className="text-gray-500">Inputs Used: {userInputs.length} / 40</p>
        </div>

        <div className="button-container">
          {/* <button onClick={sendInput} className="btn submit-btn">{loading ? 'Processing...' : 'Submit'}</button> */}
          <button onClick={() => sendInput(userInput)} className="btn submit-btn">{loading ? 'Processing...' : 'Submit'}</button>
          <button onClick={toggleMute} className="btn mute-btn">
            <img src={isMuted ? '/unmute-icon.svg' : '/mute-icon.svg'} alt={isMuted ? 'Unmute' : 'Mute'} />
          </button>
          <button onClick={startRecording} className="btn audio-btn">
            <img src="/audio-icon.svg" alt="Start Recording" />
          </button>
          <button onClick={abortSimulation} className="btn abort-btn">Abort</button>
        </div>
        {/* <div className="image-buttons">
                {images.map((image, index) => (
                    <button 
                        key={index} 
                        onClick={() => handleImageClick(image)} 
                        className="btn btn-secondary m-2">
                        Media {index + 1}
                    </button>
                ))}
            </div>
            {currentImage && (
                <div className="image-modal">
                    <img src={currentImage} alt="Case Media" />
                    <button onClick={() => setCurrentImage(null)}>Close</button>
                </div>
            )} */}
<div className="image-buttons">
  {isClinicalDecisionMaking && media0Url && (
    <button
      onClick={() => setCurrentImage(media0Url)}
      className="btn btn-secondary m-2"
    >
      Media 0
    </button>
  )}

  {filteredImages.map((url, index) => (
    <button
      key={index}
      onClick={() => setCurrentImage(url)}
      className="btn btn-secondary m-2"
    >
      Media {index + 1}
    </button>
  ))}
</div>


          {currentImage && (
            <div style={{ position:'fixed', top:0, left:0, width:'100vw', height:'100vh', backgroundColor:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', padding:'1rem', zIndex:1000 }}>
              <div style={{ position:'relative', maxWidth:'800px', height:'100%', maxHeight:'90vh' }}>
                <button onClick={() => setCurrentImage(null)} style={{ position:'absolute', top:'-1.1rem', right:'-1rem', backgroundColor:'rgba(0,0,0,0.8)', color:'#fff', border:'none', borderRadius:'50%', width:'35px', height:'35px', fontSize:'18px', cursor:'pointer', zIndex:1001 }}>&times;</button>
                {renderMedia(currentImage)}
              </div>
            </div>
          )}
        <p><strong>Proctor/Sim:</strong> {proctorResponse}</p>
        {audioSrc && !isMuted && (
          <div className="audio-container">
            <audio controls src={audioSrc} autoPlay />
          </div>
        )}
              {/* Spinner */}
      {loading && (
        <div className="spinner-container">
          <img src="/spinner.gif" alt="Loading..." className="spinner" />
        </div>
      )}

      </div>
      
      )}
      
      <div className="results"></div>
    </div>
  );
}

export default CaseSimulation;
